import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Form, Button, Table, Badge, Pagination, Offcanvas } from "react-bootstrap";
import { getMailRequests, getMailRequestById, MailRequestItem } from "../../services/adminLogs";
import { formatDateTime } from "../../utils/dateFormat";
import { useUserCountry } from "../../hooks/useUserCountry";

function StatusBadge({ status }: { status: MailRequestItem["status"] }) {
  const variant =
    status === "sent"
      ? "success"
      : status === "failed"
        ? "danger"
        : status === "processing"
          ? "info"
          : status === "queued"
            ? "secondary"
            : "dark";
  return <Badge bg={variant}>{status}</Badge>;
}

const MailRequestDrawer: React.FC<{
  id: string | null;
  onClose: () => void;
  country: string | null;
}> = ({ id, onClose, country }) => {
  const [item, setItem] = useState<MailRequestItem | null>(null);
  useEffect(() => {
    if (!id) return;
    getMailRequestById(id)
      .then(setItem)
      .catch(() => setItem(null));
  }, [id]);
  return (
    <Offcanvas show={!!id} onHide={onClose} placement="end">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Mail Request Details</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {!item ? (
          <div>Loading...</div>
        ) : (
          <div className="small">
            <div className="mb-2">
              <strong>Type:</strong> {item.type}
            </div>
            <div className="mb-2">
              <strong>Status:</strong> <StatusBadge status={item.status} />
            </div>
            <div className="mb-2">
              <strong>To:</strong> {item.to.join(", ")}
            </div>
            <div className="mb-2">
              <strong>Reply-To:</strong> {item.replyTo || "(default)"}
            </div>
            <div className="mb-2">
              <strong>Template:</strong> {item.template}
            </div>
            <div className="mb-2">
              <strong>Attempts:</strong> {item.attempts}
            </div>
            {item.lastError?.message && (
              <div className="mb-2">
                <strong>Last Error:</strong> {item.lastError.message}
              </div>
            )}
            <div className="mb-2">
              <strong>Created:</strong> {formatDateTime(item.createdAt, country)}
            </div>
            <div className="mb-2">
              <strong>Updated:</strong> {formatDateTime(item.updatedAt, country)}
            </div>
            <div className="mb-2">
              <strong>Payload:</strong>
              <pre className="mt-1 bg-light p-2 border rounded" style={{ whiteSpace: "pre-wrap" }}>
                {JSON.stringify(item.payload, null, 2)}
              </pre>
            </div>
            <div className="mb-2">
              <strong>ProviderMeta:</strong>
              <pre className="mt-1 bg-light p-2 border rounded" style={{ whiteSpace: "pre-wrap" }}>
                {JSON.stringify(item.providerMeta, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

const MailRequestsPage: React.FC = () => {
  const [items, setItems] = useState<MailRequestItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);
  const [type, setType] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [from, setFrom] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState<number>(1);
  const country = useUserCountry();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMailRequests({
        page,
        limit,
        type: type || undefined,
        status: status || undefined,
        to: to || undefined,
        from: from || undefined,
        toDate: toDate || undefined,
      });
      setItems(data.items || []);
      setTotalPages(data.meta?.totalPages || 1);
    } catch (e: any) {
      setError(e.message || "Failed to load mail requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const tableRows = useMemo(
    () =>
      items.map((it) => (
        <tr key={it._id}>
          <td>{formatDateTime(it.createdAt, country)}</td>
          <td>{it.type}</td>
          <td>{it.to.join(", ")}</td>
          <td>
            <StatusBadge status={it.status} />
          </td>
          <td>{it.attempts}</td>
          <td className="text-truncate mail-requests-error-cell">{it.lastError?.message || ""}</td>
          <td>
            <Button size="sm" variant="outline-primary" onClick={() => setSelectedId(it._id)}>
              View
            </Button>
          </td>
        </tr>
      )),
    [items, country],
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Mail Requests</h3>
        <Button size="sm" variant="outline-secondary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </Button>
      </div>

      <Row className="g-2 mb-3">
        <Col md={2}>
          <Form.Select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">All Types</option>
            <option value="verify-email">verify-email</option>
            <option value="reset-password">reset-password</option>
            <option value="broadcast">broadcast</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="queued">queued</option>
            <option value="processing">processing</option>
            <option value="sent">sent</option>
            <option value="failed">failed</option>
            <option value="cancelled">cancelled</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Control
            placeholder="To (email)"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Col>
        <Col md={2}>
          <Form.Control type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </Col>
        <Col md={1}>
          <Button
            className="w-100"
            onClick={() => {
              setPage(1);
              load();
            }}
          >
            Filter
          </Button>
        </Col>
      </Row>

      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>To</th>
            <th>Status</th>
            <th>Attempts</th>
            <th>Last Error</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {error ? (
            <tr>
              <td colSpan={7} className="text-danger">
                {error}
              </td>
            </tr>
          ) : tableRows.length ? (
            tableRows
          ) : (
            <tr>
              <td colSpan={7}>{loading ? "Loading..." : "No results"}</td>
            </tr>
          )}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center">
        <div className="small text-muted">
          Page {page} of {totalPages}
        </div>
        <div className="d-flex align-items-center gap-2">
          <Form.Select
            size="sm"
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            style={{ width: 100 }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
          <Pagination className="mb-0">
            <Pagination.Prev
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            />
            <Pagination.Next disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} />
          </Pagination>
        </div>
      </div>

      <MailRequestDrawer id={selectedId} onClose={() => setSelectedId(null)} country={country} />
    </div>
  );
};

export default MailRequestsPage;
